import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslate } from "../../../../language/i18n";
import { setError } from "../../../../reducers/dialog/dialogAction";

export const NON_UI_LOGIN_METHODS = {
  AZURE_AD: "AZURE_AD",
} as const;

export const UI_LOGIN_METHODS = {
  LOGIN_FORM: "LOGIN_FORM",
  BANKID: "BANKID",
  ENTRA: "ENTRA",
  OPEN_ID: "OPEN_ID",
} as const;

export const LOGIN_METHODS = {
  ...NON_UI_LOGIN_METHODS,
  ...UI_LOGIN_METHODS,
} as const;

export type LoginMethod = keyof typeof LOGIN_METHODS;

const useAvailableLoginMethods = () => {
  const [loginMethodsInitialized, setLoginMethodsInitialized] = useState(false);
  const [availableLoginMethods, setAvailableLoginMethods] = useState<LoginMethod[]>([]);
  const translate = useTranslate();

  const isValidLoginMethod = (loginMethod: string): loginMethod is LoginMethod => {
    return loginMethod in LOGIN_METHODS;
  };

  const isAllowedLoginMethod = (loginMethod: string): loginMethod is LoginMethod => {
    return isValidLoginMethod(loginMethod) && availableLoginMethods.includes(loginMethod);
  };

  const addLoginMethod = useCallback(
    (loginMethod: string) => {
      if (isValidLoginMethod(loginMethod) && !availableLoginMethods.includes(loginMethod)) {
        setAvailableLoginMethods(prevMethods => [...prevMethods, loginMethod]);
      }
    },
    [availableLoginMethods],
  );

  useEffect(() => {
    if (process.env.REACT_APP_LOGIN_METHODS) {
      const loginMethods = process.env.REACT_APP_LOGIN_METHODS.split(",");

      if (loginMethods.length === 0) {
        setError({ error: "exception", errorMessage: translate("NO_AUTHENTICATION_METHOD_DEFINED") });

        return;
      }

      loginMethods.forEach(addLoginMethod);
    } else {
      addLoginMethod(UI_LOGIN_METHODS.LOGIN_FORM);
    }

    setLoginMethodsInitialized(true);
  }, [addLoginMethod, translate]);

  const availableUILoginMethods = useMemo(
    () => availableLoginMethods.filter((method): method is keyof typeof UI_LOGIN_METHODS => method in UI_LOGIN_METHODS),
    [availableLoginMethods],
  );

  return { availableLoginMethods, availableUILoginMethods, loginMethodsInitialized, isAllowedLoginMethod };
};

export default useAvailableLoginMethods;
